import React, { useState, useCallback, useEffect, useRef } from "react";
import PublicLayout from "../../components/PublicLayout";
import {
  MapPin,
  Navigation,
  Copy,
  Check,
  Phone,
  GalleryVerticalEnd,
  Heater,
  Car,
  Droplets,
  DoorClosedLocked,
  X,
} from "lucide-react";
import ReactMapGL, { Marker, Popup, FlyToInterpolator } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// 1. Datele Hardcodate - Actualizate conform Abonamentelor
const LOCATIONS = [
  {
    id: "fiald",
    name: "Bazin Fiald (Hotel Fiald)",
    address: "Strada Tazlăului 7A, Bacău, 600372",
    latitude: 46.58216328636135,
    longitude: 26.90225368249042,
    facilities: ["Vestiare", "Dușuri", "Recepție", "Parcare în zonă", "SPA"],
    levels: [
      "Copii",
      "Adulți",
      "Personal Training",
      "Performanță",
      "BabySwim (2-4 ani)",
    ],
    note: "Acces prin recepția hotelului.",
    is_highlighted: true,
    photos: [
      "https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?auto=format&fit=crop&q=80&w=900",
      "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&q=80&w=900",
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&q=80&w=900",
    ],
  },
  {
    id: "olimpic",
    name: "Bazinul Olimpic Bacău",
    address: "Aleea Ghioceilor 10-14, Bacău, 600156",
    latitude: 46.55783882908194,
    longitude: 26.918723801004784,
    facilities: ["Vestiare", "Dușuri", "Tribună", "Parcare"],
    levels: ["Copii", "Adulți"],
    note: "Programul poate varia în funcție de disponibilitatea bazinului (competiții sportive).",
    photos: [
      "https://images.unsplash.com/photo-1600965962361-9035dbfd1c50?auto=format&fit=crop&q=80&w=900",
      "https://images.unsplash.com/photo-1530549387789-4c1017266635?auto=format&fit=crop&q=80&w=900",
      "https://images.unsplash.com/photo-1519315901367-f34ff9154487?auto=format&fit=crop&q=80&w=900",
    ],
  },
  {
    id: "emd",
    name: "Bazinul EMD Academy",
    address: "Bulevardul Unirii 43, Bacău, 600398",
    latitude: 46.57456179692856,
    longitude: 26.92605471702549,
    facilities: ["Vestiare", "Dușuri", "SPA", "Parcare"],
    levels: ["Copii"],
    note: "Contact locație: office@complexemd.ro",
    photos: [
      "https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?auto=format&fit=crop&q=80&w=900",
      "https://images.unsplash.com/photo-1565992441121-4367c2967103?auto=format&fit=crop&q=80&w=900",
      "https://images.unsplash.com/photo-1574620474670-77c5a4b3f99f?auto=format&fit=crop&q=80&w=900",
    ],
  },
];

const facilityIcons = {
  Vestiare: DoorClosedLocked,
  Dușuri: Droplets,
  Parcare: Car,
  "Parcare în zonă": Car,
  SPA: Heater,
  Recepție: Phone,
  Tribună: GalleryVerticalEnd,
};

// Token-ul public Mapbox (pk.*) e gândit să fie expus în frontend.
// Curățăm valoarea din env (ghilimele / spații lipite accidental la copy-paste în Render)
// și, dacă nu arată ca un token public valid, folosim fallback-ul hardcodat verificat.
const _envToken = (process.env.REACT_APP_MAPBOX_TOKEN || "")
  .trim()
  .replace(/^["']+|["']+$/g, "");
const MAPBOX_TOKEN = _envToken.startsWith("pk.")
  ? _envToken
  : "pk.eyJ1IjoiZ2VvcmdlY2lwIiwiYSI6ImNsZzJyc3JqYjA4ZmgzaGxvZTQxNnoycWIifQ.MM3ytFfHZqkdJZUB9lkJtQ";

// Fotografii reale ale locațiilor: pune fișierele în frontend/public/locations/<id>/1.jpg ... 6.jpg
// (id = fiald / olimpic / emd). Dacă există, înlocuiesc automat pozele generice de mai sus.
const MAX_REAL_PHOTOS = 6;

const useRealPhotos = (locations) => {
  const [photoMap, setPhotoMap] = useState({});

  useEffect(() => {
    let cancelled = false;
    locations.forEach((loc) => {
      const candidates = Array.from(
        { length: MAX_REAL_PHOTOS },
        (_, i) => `/locations/${loc.id}/${i + 1}.jpg`,
      );
      Promise.all(
        candidates.map(
          (src) =>
            new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve(src);
              img.onerror = () => resolve(null);
              img.src = src;
            }),
        ),
      ).then((results) => {
        const found = results.filter(Boolean);
        if (!cancelled && found.length > 0) {
          setPhotoMap((prev) => ({ ...prev, [loc.id]: found }));
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, [locations]);

  return photoMap;
};

// Galerie cu swipe (touch) și drag (mouse); overlay-urile se dau ca children
const SwipeGallery = ({ photos, alt, index, onIndexChange, className = "", children }) => {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  const onPointerDown = (e) => {
    if (photos.length < 2) return;
    dragRef.current = { startX: e.clientX, width: e.currentTarget.offsetWidth };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setDragX(e.clientX - dragRef.current.startX);
  };

  const endDrag = () => {
    if (!dragRef.current) return;
    const threshold = dragRef.current.width * 0.15;
    if (dragX <= -threshold && index < photos.length - 1) onIndexChange(index + 1);
    else if (dragX >= threshold && index > 0) onIndexChange(index - 1);
    dragRef.current = null;
    setDragging(false);
    setDragX(0);
  };

  // Rezistență la capete (efect elastic)
  const atEdge =
    (index === 0 && dragX > 0) || (index === photos.length - 1 && dragX < 0);
  const offset = dragging ? (atEdge ? dragX / 3 : dragX) : 0;

  return (
    <div
      className={`relative overflow-hidden select-none touch-pan-y ${
        photos.length > 1 ? "cursor-grab active:cursor-grabbing" : ""
      } ${className}`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div
        className="flex h-full"
        style={{
          transform: `translateX(calc(${-index * 100}% + ${offset}px))`,
          transition: dragging
            ? "none"
            : "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {photos.map((src, i) => (
          <div key={i} className="w-full h-full flex-shrink-0 overflow-hidden">
            <img
              src={src}
              alt={`${alt} - foto ${i + 1}`}
              draggable={false}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
              onError={(e) => {
                e.target.style.visibility = "hidden";
              }}
            />
          </div>
        ))}
      </div>
      {children}
    </div>
  );
};

const LocationsPage = () => {
  const [selectedLocation, setSelectedLocation] = useState(
    LOCATIONS.find((l) => l.is_highlighted).id,
  );
  const [copied, setCopied] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true,
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [viewport, setViewport] = useState({
    latitude: 46.571,
    longitude: 26.914,
    zoom: 12.3,
    pitch: 45,
    bearing: -20,
    width: "100%",
    height: "100%",
  });

  const selected = LOCATIONS.find((l) => l.id === selectedLocation);
  const realPhotos = useRealPhotos(LOCATIONS);
  const photosFor = (loc) => realPhotos[loc.id] || loc.photos || [];
  const selectedPhotos = selected ? photosFor(selected) : [];

  // Funcția de zbor către locație
  const onSelectLocation = useCallback((location) => {
    setSelectedLocation(location.id);
    setShowPopup(true);
    setPhotoIndex(0);
    setIsAnimating(true);

    setViewport((prev) => ({
      ...prev,
      longitude: location.longitude,
      // Ușor deplasat spre nord ca popup-ul (ancorat deasupra marcajului) să încapă în hartă
      latitude: location.latitude + 0.0047,
      zoom: 14.5,
      pitch: 45,
      transitionDuration: 900,
      transitionInterpolator: new FlyToInterpolator(),
    }));
  }, []);

  useEffect(() => {
    if (!isAnimating) return;
    const timer = setTimeout(() => {
      setIsAnimating(false);
      setViewport((prev) => {
        const { transitionDuration, transitionInterpolator, ...rest } = prev;
        return rest;
      });
    }, 900);
    return () => clearTimeout(timer);
  }, [isAnimating]);

  // Funcție pentru adăugarea stratului de clădiri 3D
  const onMapLoad = useCallback((evt) => {
    const map = evt.target;

    const layers = map.getStyle().layers;
    const labelLayerId = layers.find(
      (layer) => layer.type === "symbol" && layer.layout["text-field"],
    )?.id;

    map.addLayer(
      {
        id: "3d-buildings",
        source: "composite",
        "source-layer": "building",
        filter: ["==", "extrude", "true"],
        type: "fill-extrusion",
        minzoom: 14,
        paint: {
          "fill-extrusion-color": "#1a1a1a",
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.8,
        },
      },
      labelLayerId,
    );
  }, []);

  const handleCopyAddress = () => {
    if (selected) {
      navigator.clipboard.writeText(selected.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNavigate = () => {
    if (!selected) return;

    const { latitude, longitude, name } = selected;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = `geo:${latitude},${longitude}?q=${encodeURIComponent(name)}`;
    } else {
      window.open(
        `http://googleusercontent.com/maps.google.com/maps?q=${latitude},${longitude}`,
        "_blank",
      );
    }
  };

  const handleViewportChange = useCallback((nextViewport) => {
    setIsAnimating(false);
    const { transitionDuration, transitionInterpolator, ...rest } = nextViewport;
    setViewport(rest);
  }, []);

  return (
    <PublicLayout>
      <section className="pt-32 pb-8 bg-[#050505]" data-testid="locations-hero">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-[#6db025] text-xs font-heading uppercase tracking-[0.3em] mb-4 block">
            Locații
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-tight mb-4">
            Bazinele Noastre
          </h1>
          <p className="text-white/60 text-xl max-w-2xl">
            Antrenamente în cele mai bune bazine din Bacău. Alege locația cea
            mai convenabilă pentru tine.
          </p>
        </div>
      </section>

      <section
        className="pb-20 bg-[#050505]"
        data-testid="locations-map-section"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="order-2 lg:order-1">
              <div className="map-container border border-white/10 h-[450px] lg:h-[600px] relative overflow-hidden bg-[#121212]">
                {MAPBOX_TOKEN ? (
                  <ReactMapGL
                    {...viewport}
                    mapboxApiAccessToken={MAPBOX_TOKEN}
                    onViewportChange={handleViewportChange}
                    mapStyle="mapbox://styles/mapbox/dark-v11"
                    onLoad={onMapLoad}
                    onError={(e) => console.error("Mapbox error:", e?.error || e)}
                    dragPan={true}
                    scrollZoom={true}
                  >
                    {LOCATIONS.map((location) => {
                      const isSelected = selectedLocation === location.id;
                      const isHighlighted = location.is_highlighted;

                      return (
                        <Marker
                          key={location.id}
                          longitude={location.longitude}
                          latitude={location.latitude}
                          offsetLeft={-16}
                          offsetTop={-16}
                        >
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectLocation(location);
                            }}
                            className={`cursor-pointer transition-all ${isSelected ? "scale-125 z-10" : "hover:scale-110"}`}
                          >
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isHighlighted
                                  ? "bg-[#6db025] shadow-[0_0_20px_rgba(204,255,0,0.5)]"
                                  : isSelected
                                    ? "bg-[#6db025]"
                                    : "bg-white/20 backdrop-blur-md hover:bg-white/40"
                              }`}
                            >
                              <MapPin
                                size={16}
                                className={
                                  isHighlighted || isSelected
                                    ? "text-black"
                                    : "text-white"
                                }
                              />
                            </div>
                            {isSelected && (
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 whitespace-nowrap z-20">
                                <span className="bg-[#6db025] text-black text-[10px] font-heading font-bold uppercase px-2 py-1 shadow-lg">
                                  {location.name
                                    .replace("Bazin ", "")
                                    .replace("Bazinul ", "")}
                                </span>
                              </div>
                            )}
                          </div>
                        </Marker>
                      );
                    })}

                    {isDesktop && showPopup && selected && (
                      <Popup
                        latitude={selected.latitude}
                        longitude={selected.longitude}
                        anchor="bottom"
                        offsetTop={-18}
                        closeButton={false}
                        closeOnClick={false}
                        onClose={() => setShowPopup(false)}
                        dynamicPosition={false}
                        className="location-popup"
                        captureScroll={true}
                      >
                        <div
                          key={selected.id}
                          className="w-[300px] bg-[#0A0A0A] border border-white/10 overflow-hidden shadow-2xl"
                        >
                          {/* Galerie foto - swipe / drag pentru navigare */}
                          <SwipeGallery
                            photos={selectedPhotos}
                            alt={selected.name}
                            index={photoIndex}
                            onIndexChange={setPhotoIndex}
                            className="h-40 bg-[#121212]"
                          >
                            {/* Săgeți galerie */}
                            {selectedPhotos.length > 1 && (
                              <>
                                <button
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={() =>
                                    setPhotoIndex(
                                      (p) =>
                                        (p - 1 + selectedPhotos.length) %
                                        selectedPhotos.length,
                                    )
                                  }
                                  className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-black/60 hover:bg-[#6db025] hover:text-black text-white rounded-full transition-colors"
                                  aria-label="Foto anterioară"
                                >
                                  ‹
                                </button>
                                <button
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={() =>
                                    setPhotoIndex(
                                      (p) => (p + 1) % selectedPhotos.length,
                                    )
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-black/60 hover:bg-[#6db025] hover:text-black text-white rounded-full transition-colors"
                                  aria-label="Foto următoare"
                                >
                                  ›
                                </button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                                  {selectedPhotos.map((_, i) => (
                                    <button
                                      key={i}
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onClick={() => setPhotoIndex(i)}
                                      className={`h-1.5 rounded-full transition-all ${
                                        i === photoIndex
                                          ? "w-4 bg-[#6db025]"
                                          : "w-1.5 bg-white/40 hover:bg-white/70"
                                      }`}
                                      aria-label={`Foto ${i + 1}`}
                                    />
                                  ))}
                                </div>
                              </>
                            )}

                            {/* Buton închidere */}
                            <button
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={() => setShowPopup(false)}
                              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/60 hover:bg-red-500 text-white rounded-full transition-colors"
                              aria-label="Închide"
                            >
                              <X size={14} />
                            </button>

                            {selected.is_highlighted && (
                              <span className="absolute top-2 left-2 bg-[#6db025] text-black text-[10px] font-black uppercase tracking-wider px-2 py-1">
                                Sediu Principal
                              </span>
                            )}
                          </SwipeGallery>

                          {/* Conținut - elementele intră decalat (stagger) */}
                          <div className="p-4 space-y-3 popup-stagger">
                            <h3 className="font-heading text-lg font-bold text-white uppercase leading-tight">
                              {selected.name}
                            </h3>
                            <p className="text-white/55 text-sm flex items-start gap-2 leading-snug">
                              <MapPin
                                size={15}
                                className="flex-shrink-0 mt-0.5 text-[#6db025]"
                              />
                              {selected.address}
                            </p>

                            <div className="flex flex-wrap gap-1.5">
                              {selected.facilities?.map((facility, i) => {
                                const Icon = facilityIcons[facility] || Check;
                                return (
                                  <span
                                    key={i}
                                    className="flex items-center gap-1 text-xs text-white/60 bg-white/5 px-2 py-1 rounded-sm"
                                  >
                                    <Icon size={12} className="text-[#6db025]" />
                                    {facility}
                                  </span>
                                );
                              })}
                            </div>

                            <div className="flex flex-wrap gap-1.5">
                              {selected.levels?.map((level, i) => (
                                <span
                                  key={i}
                                  className="text-[10px] text-[#6db025] border border-[#6db025]/20 bg-[#6db025]/10 px-2 py-0.5 uppercase font-bold tracking-wide rounded-sm"
                                >
                                  {level}
                                </span>
                              ))}
                            </div>

                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={handleNavigate}
                                className="flex-1 bg-[#6db025] text-black px-3 py-2.5 flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:bg-white transition-colors"
                              >
                                <Navigation size={14} />
                                Navigare
                              </button>
                              <button
                                onClick={handleCopyAddress}
                                className="bg-white/10 text-white px-3 py-2.5 flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:bg-white/20 transition-colors"
                              >
                                {copied ? (
                                  <Check size={14} className="text-[#6db025]" />
                                ) : (
                                  <Copy size={14} />
                                )}
                                {copied ? "Copiat" : "Copiază"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </Popup>
                    )}
                  </ReactMapGL>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center p-6">
                    <div className="text-[#6db025]/20 font-heading text-6xl mb-4">
                      BACĂU
                    </div>
                    <p className="text-white/50 text-sm">
                      Harta necesită Mapbox Token.
                    </p>
                    <p className="text-white/30 text-xs mt-2">
                      Adaugă REACT_APP_MAPBOX_TOKEN în fișierul .env
                    </p>
                  </div>
                )}
              </div>

              <p className="hidden lg:block mt-4 text-white/35 text-sm">
                Apasă pe un marcaj de pe hartă pentru detalii și fotografii.
              </p>
            </div>

            <div className="order-1 lg:order-2 space-y-4">
              {LOCATIONS.map((location) => {
                const isSelected = selectedLocation === location.id;
                const locPhotos = photosFor(location);
                return (
                  <div
                    key={location.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectLocation(location)}
                    onKeyDown={(e) => e.key === 'Enter' && onSelectLocation(location)}
                    className={`w-full text-left p-6 border transition-all cursor-pointer ${
                      isSelected
                        ? "bg-[#6db025]/5 border-[#6db025]/50"
                        : "bg-[#0A0A0A] border-white/5 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        {location.is_highlighted && (
                          <span className="inline-block bg-[#6db025] text-black text-[10px] font-bold uppercase px-2 py-1 mb-2">
                            Sediu Principal
                          </span>
                        )}
                        <h3
                          className={`font-heading text-xl font-bold uppercase mb-2 transition-colors ${isSelected ? "text-[#6db025]" : "text-white"}`}
                        >
                          {location.name}
                        </h3>
                        <p className="text-white/50 text-base flex items-start gap-2">
                          <MapPin size={15} className="flex-shrink-0 mt-1" />
                          {location.address}
                        </p>
                      </div>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? "bg-[#6db025]" : "bg-white/5"
                        }`}
                      >
                        <MapPin
                          size={16}
                          className={
                            isSelected ? "text-black" : "text-white/30"
                          }
                        />
                      </div>
                    </div>

                    {isSelected && (
                      <div className="lg:hidden mt-6 pt-6 border-t border-white/10 animate-fade-in">
                        {/* Galerie foto pe mobil - swipe pentru navigare */}
                        {locPhotos.length > 0 && (
                          <SwipeGallery
                            photos={locPhotos}
                            alt={location.name}
                            index={photoIndex % locPhotos.length}
                            onIndexChange={setPhotoIndex}
                            className="h-44 mb-4 bg-[#121212] border border-white/5"
                          >
                            {locPhotos.length > 1 && (
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
                                {locPhotos.map((_, i) => (
                                  <button
                                    key={i}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => { e.stopPropagation(); setPhotoIndex(i); }}
                                    className={`h-2 rounded-full transition-all ${
                                      i === photoIndex % locPhotos.length ? "w-5 bg-[#6db025]" : "w-2 bg-white/40"
                                    }`}
                                    aria-label={`Foto ${i + 1}`}
                                  />
                                ))}
                              </div>
                            )}
                          </SwipeGallery>
                        )}

                        {location.note && (
                          <p className="text-white/50 text-base mb-4">
                            {location.note}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2 mb-4">
                          {location.facilities?.map((facility, i) => {
                            const Icon = facilityIcons[facility] || Check;
                            return (
                              <span
                                key={i}
                                className="flex items-center gap-1 text-xs text-white/50 bg-white/5 px-3 py-1.5 rounded-sm"
                              >
                                <Icon size={12} className="text-[#6db025]" />
                                {facility}
                              </span>
                            );
                          })}
                        </div>

                        <div className="flex flex-wrap gap-2 mb-6">
                          {location.levels?.map((level, i) => (
                            <span
                              key={i}
                              className="text-[10px] text-[#6db025] border border-[#6db025]/20 bg-[#6db025]/10 px-2.5 py-1 uppercase font-bold tracking-wide rounded-sm"
                            >
                              {level}
                            </span>
                          ))}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigate();
                            }}
                            className="bg-[#6db025] text-black px-4 py-3 flex justify-center items-center gap-2 text-sm font-heading font-bold uppercase w-full"
                          >
                            <Navigation size={14} />
                            Navigare
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyAddress();
                            }}
                            className="bg-white/10 text-white px-4 py-3 flex justify-center items-center gap-2 text-sm font-heading font-bold uppercase w-full"
                          >
                            {copied ? (
                              <Check size={14} className="text-[#6db025]" />
                            ) : (
                              <Copy size={14} />
                            )}
                            {copied ? "Copiat" : "Copiază"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LocationsPage;
